import React, { useEffect, useRef, useState } from 'react';
import { PlayIcon, PauseIcon, ArrowUpRightIcon } from 'lucide-react';
import { BrandIcon } from './BrandIcon';

// Branded SoundCloud player: the official widget iframe is kept as a HIDDEN audio
// engine (SoundCloud's white UI never shows); we render a FAR-skinned dark player
// on top of the Widget API.
//
// HEADER CIRCLE = THE PLATFORM MARK (Ryan, Aug 31: "SoundCloud icons in both the
// astrofreebie and moon mix playlists in the circle"). At rest the circle carries
// the SoundCloud logo; while that playlist is playing it becomes a PAUSE control,
// so the card still has a master stop. Per-track control lives on each row.
//
// PER-TRACK PLAY/PAUSE (Ryan, Aug 31: "we need a play/pause button on the
// astrofreebies and moonmix playlists"). Every row's left slot is a real control:
//   * the loaded row shows PAUSE while playing / PLAY while paused, and toggles it
//   * every other row shows its track number, swapping to a play glyph on
//     hover/focus (.scp-pp in records-players.css - CSS rather than Tailwind hover
//     variants so the touch case is explicit: no hover, so the number just stays)
//   * pressing anywhere else on the row still starts that track
//
// COVER ART (Ryan, Aug 31): the same 20px thumb the Spotify card uses. getSounds()
// returns `artwork_url` per sound (SoundCloud serves it at -large = 100x100); we
// request the -t67x67 crop instead so a 20px square is not fed a 100px file. A track
// with no art falls back to the uploader avatar; with neither, the row has no thumb.
//
// FULL TRACKLIST (Ryan, Aug 31: "only showing a few"): the widget lazy-loads track
// metadata only for rows inside ITS OWN viewport. A 2x2px hidden iframe therefore
// only ever loaded ~5 titles. Fix: the hidden iframe is now rendered LARGE (600x4000)
// and parked off-screen, so the widget lays out every row and loads every title;
// getSounds() is then polled until no title is missing (or 20s). Rows whose title
// hasn't arrived yet show as numbered placeholders instead of being dropped.

declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interface Window {
    SC?: any;
  }
}
let apiPromise: Promise<void> | null = null;
function loadApi(): Promise<void> {
  if (!apiPromise) {
    apiPromise = new Promise((resolve, reject) => {
      if (window.SC?.Widget) return resolve();
      const s = document.createElement('script');
      s.src = 'https://w.soundcloud.com/player/api.js';
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('sc api blocked'));
      document.head.appendChild(s);
    });
  }
  return apiPromise;
}
const clean = (t?: string) => (t || '').replace(/^Future Astronaut Records\s*-\s*/i, '');
const fmt = (ms: number) => {
  if (!ms) return '';
  const m = Math.floor(ms / 60000);
  const s = Math.floor(ms % 60000 / 1000);
  return `${m}:${String(s).padStart(2, '0')}`;
};
// SoundCloud hands back .../artworks-xxx-large.jpg (100x100). Ask for the small
// square crop instead so a 20px thumb is not downloading a 100px file per row.
const art = (u?: string) => u ? u.replace(/-large\.(jpg|png)/, '-t67x67.$1') : '';
type Track = {
  idx: number;
  title: string;
  duration: number;
  loaded: boolean;
  art: string;
};
type Props = {
  src: string;
  open: string;
  title: string;
  eyebrow: string;
  accent: string;
};
export function SoundCloudPlayer({
  src,
  open,
  title,
  eyebrow,
  accent
}: Props) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const widgetRef = useRef<any>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [cur, setCur] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [prog, setProg] = useState(0);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let dead = false;
    let poll: ReturnType<typeof setInterval> | null = null;
    const to = setTimeout(() => {
      if (!widgetRef.current) setFailed(true);
    }, 6000);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const refresh = (w: any) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      w.getSounds((ss: any[]) => {
        if (dead) return;
        const rows: Track[] = (ss || []).map((t, i) => ({
          idx: i,
          title: clean(t?.title),
          duration: t?.duration || 0,
          loaded: !!t?.title,
          art: art(t?.artwork_url || t?.user?.avatar_url)
        }));
        if (rows.length) setTracks(rows);
        if (rows.length && rows.every(r => r.loaded) && poll) {
          clearInterval(poll);
          poll = null;
        }
      });
    };
    loadApi().then(() => {
      if (dead || !frameRef.current || !window.SC?.Widget) return;
      const w = window.SC.Widget(frameRef.current);
      widgetRef.current = w;
      const E = window.SC.Widget.Events;
      w.bind(E.READY, () => {
        clearTimeout(to);
        refresh(w);
        // keep asking until every title has arrived (widget loads them lazily)
        let n = 0;
        poll = setInterval(() => {
          n += 1;
          refresh(w);
          if (n > 13 && poll) {
            clearInterval(poll);
            poll = null;
          }
        }, 1500);
      });
      w.bind(E.PLAY, () => {
        if (dead) return;
        setPlaying(true);
        refresh(w);
        w.getCurrentSoundIndex((i: number) => {
          if (!dead && typeof i === 'number') setCur(i);
        });
      });
      w.bind(E.PAUSE, () => !dead && setPlaying(false));
      w.bind(E.FINISH, () => !dead && setPlaying(false));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      w.bind(E.PLAY_PROGRESS, (e: any) => !dead && setProg(e.relativePosition || 0));
    }).catch(() => !dead && setFailed(true));
    return () => {
      dead = true;
      clearTimeout(to);
      if (poll) clearInterval(poll);
    };
  }, []);

  // Row control: toggle the loaded track, or jump to any other one (skip() plays).
  const rowToggle = (idx: number) => {
    const w = widgetRef.current;
    if (!w) return;
    if (idx === cur) w.toggle();else {
      setCur(idx);
      w.skip(idx);
    }
  };
  const current = tracks.find(t => t.idx === cur);
  const headline = failed ? 'Open the playlist on SoundCloud' : current?.loaded ? current.title : tracks[0]?.loaded ? tracks[0].title : 'Loading tracks…';
  return <div className="scp flex min-h-0 min-w-0 flex-1 flex-col gap-1.5" data-id="element-355" data-mp="1448qg5kzy734">
      {/* hidden audio engine — LARGE so the widget loads every row's metadata */}
      <iframe ref={frameRef} title={title} src={src} allow="autoplay; encrypted-media" aria-hidden="true" tabIndex={-1} style={{
      position: 'fixed',
      left: '-10000px',
      top: 0,
      width: 600,
      height: 4000,
      opacity: 0,
      pointerEvents: 'none',
      border: 0
    }} data-id="element-356" data-mp="1wswiql45xwq6" />
      {/* ONE header band: platform mark / pause, now-playing over collection line, open. */}
      <div className="flex items-center gap-2" data-id="element-357" data-mp="bfjxwuy0ba2z">
        <button type="button" aria-label={playing ? 'Pause' : 'Play the playlist'} onClick={() => widgetRef.current?.toggle()} className="flex h-8 w-8 flex-none items-center justify-center rounded-full text-white shadow-neon transition-transform duration-150 ease-expo hover:scale-105 active:scale-95" style={{
        background: 'linear-gradient(135deg, #8b5cff, #c86bff)'
      }} data-id="element-358" data-mp="bd78jwpx5xfw">
          {playing ? <PauseIcon className="h-3.5 w-3.5" aria-hidden="true" fill="currentColor" data-id="element-359" data-mp="3hbqinr3ko54b" /> : <BrandIcon name="soundcloud" className="h-[1.05rem] w-[1.05rem]" data-id="element-360" data-mp="3ccnh4gmswh13" />}
        </button>
        <div className="min-w-0 flex-1" data-id="element-361" data-mp="3r1a3316xo71a">
          <p className="truncate text-[0.8rem] font-semibold leading-tight text-white" data-id="element-362" data-mp="3r2bcz2c7qnhf">{headline}</p>
          <p className={`mt-0.5 truncate font-tech text-[0.55rem] font-semibold uppercase tracking-[0.22em] ${accent}`} data-id="element-363" data-mp="2yk7hzqx0t35g">
            {eyebrow}{tracks.length ? ` · ${tracks.length} tracks` : ''}
          </p>
        </div>
        <a href={open} target="_blank" rel="noopener noreferrer" aria-label="Open on SoundCloud" className="flex h-6 w-6 flex-none items-center justify-center rounded-md border border-white/10 text-white/50 transition-colors duration-200 hover:border-cyan/50 hover:text-cyan" data-id="element-364" data-mp="282b7gbdsivz3">
          <ArrowUpRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-365" data-mp="2m344p3vh855w" />
        </a>
      </div>
      {/* the rail IS the divider - the list carries no border of its own */}
      <div className="h-[2px] flex-none overflow-hidden rounded-full bg-white/10" data-id="element-366" data-mp="2vyysedek4qfj">
        <i className="block h-full" style={{
        width: `${prog * 100}%`,
        background: 'linear-gradient(100deg, #37e5ff 0%, #c86bff 48%, #8b5cff 100%)',
        transition: 'width .3s linear'
      }} data-id="element-367" data-mp="30qp9d2802cfx" />
      </div>
      {/* tracklist — compact rows: play/pause, cover, title, length */}
      <ol className="scp-list min-h-0 flex-1 list-none overflow-y-auto p-0" data-id="element-368" data-mp="3eqrle7tmmbn9">
        {tracks.map(t => {
        const active = t.idx === cur;
        return <li key={t.idx} onClick={() => rowToggle(t.idx)} className={`flex cursor-pointer items-center gap-2 border-b border-white/5 px-1 py-1 transition-colors duration-150 hover:bg-violet/10 ${active ? 'bg-violet/10' : ''}`} data-id="element-369" data-mp="y3iii0x84wne">
              <button type="button" aria-label={active && playing ? `Pause ${t.title}` : `Play ${t.loaded ? t.title : 'track ' + (t.idx + 1)}`} onClick={e => {
            e.stopPropagation();
            rowToggle(t.idx);
          }} className={`scp-pp relative flex h-5 w-5 flex-none items-center justify-center rounded-full transition-colors duration-150 ${active ? 'bg-violet/30 text-cyan' : 'text-white/35 hover:bg-white/10 hover:text-white'}`} data-id="element-370" data-mp="1lt4e6eee0tqn">
                {active ? playing ? <PauseIcon className="h-2.5 w-2.5" aria-hidden="true" fill="currentColor" data-id="element-371" data-mp="20ok2e2sadvf4" /> : <PlayIcon className="h-2.5 w-2.5" aria-hidden="true" fill="currentColor" data-id="element-372" data-mp="qgippla1y9y1" /> : <>
                    <span className="scp-n font-tech text-[0.54rem]" data-id="element-373" data-mp="14hv5nwp7qbjx">{String(t.idx + 1).padStart(2, '0')}</span>
                    <PlayIcon className="scp-i absolute h-2.5 w-2.5" aria-hidden="true" fill="currentColor" data-id="element-374" data-mp="10df0xyrc5a6a" />
                  </>}
              </button>
              {t.art ? <img src={t.art} alt="" loading="lazy" className={`h-5 w-5 flex-none rounded-[3px] object-cover ${active ? 'opacity-100' : 'opacity-90'}`} data-id="element-375" data-mp="19f4r719he32h" /> : null}
              <span className={`min-w-0 flex-1 truncate text-[0.72rem] leading-snug ${active ? 'text-cyan' : t.loaded ? 'text-white/75' : 'text-white/30'}`} data-id="element-376" data-mp="1x60qejg9o0jr">
                {t.loaded ? t.title : `Track ${t.idx + 1}`}
              </span>
              <span className="flex-none font-tech text-[0.56rem] text-white/35" data-id="element-377" data-mp="2bplbyw7943ma">{fmt(t.duration)}</span>
            </li>;
      })}
        {!tracks.length && !failed ? <li className="px-1 py-1 text-[0.72rem] text-white/40" data-id="element-378" data-mp="22qgcnnnv8692">Loading tracklist…</li> : null}
        {failed ? <li className="px-1 py-2 text-[0.72rem] text-white/40" data-id="element-379" data-mp="26ufse41bn11d">
            <a href={open} target="_blank" rel="noopener noreferrer" className="text-cyan" data-id="element-380" data-mp="1t0es35uq15yw">Open this playlist on SoundCloud ↗</a>
          </li> : null}
      </ol>
    </div>;
}